(() => {
  const copy = (fr, en) => document.documentElement.lang.split('-')[0] === 'en' ? en : fr;
  if (customElements.get('composition-look-buy')) return;
  customElements.define('composition-look-buy', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        if (!event.target.closest('button')) return;
        event.preventDefault();
        event.stopPropagation();
        this.openPreview();
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
    async openPreview() {
      const button = this.querySelector('button');
      if (this.busy || button.disabled || !this.dataset.url) return;
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const label = this.querySelector('[data-look-label]');
      const status = this.querySelector('[role="status"]');
      const originalLabel = label.textContent;
      label.textContent = this.dataset.loading;
      status.textContent = '';
      try {
        const pageResponse = await fetch(this.dataset.url, { cache: 'no-store', signal: this.controller.signal });
        if (!pageResponse.ok) throw new Error(this.dataset.unavailable);
        const page = new DOMParser().parseFromString(await pageResponse.text(), 'text/html');
        const composition = page.querySelector('composition-catalog-product');
        const panel = [...(composition?.querySelectorAll('[data-composition-photo]') || [])]
          .find(item => item.dataset.compositionPhoto === this.dataset.photo);
        if (!panel?.dataset.image || !panel.querySelector('[data-jewel-id]')) {
          throw new Error(this.dataset.unavailable);
        }
        if (!this.isConnected) return;
        const preview = new CustomEvent('composition-look:preview', {
          cancelable: true,
          detail: {
            title: composition.dataset.title, panel, opener: button,
            add: () => this.addToCart(composition.dataset.title, panel),
            addJewel: card => this.addJewelToCart(card)
          }
        });
        const modal = document.getElementById('CompositionModal');
        if (!modal || modal.dispatchEvent(preview)) throw new Error(this.dataset.retry);
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = error.message || this.dataset.retry;
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        label.textContent = originalLabel;
      }
    }
    async addJewelToCart(card) {
      if (this.busy) throw new Error(copy('Un ajout est déjà en cours.', 'An item is already being added.'));
      this.busy = true;
      const root = window.Shopify?.routes?.root || '/';
      try {
        if (!card.dataset.handle) throw new Error(copy('Ce bijou est indisponible.', 'This piece is unavailable.'));
        const productResponse = await fetch(`${root}products/${encodeURIComponent(card.dataset.handle)}.js`, { cache: 'no-store' });
        if (!productResponse.ok) throw new Error(copy('Impossible de vérifier ce bijou. Veuillez réessayer.', 'This piece could not be checked. Please try again.'));
        const product = await productResponse.json();
        const variant = product.variants?.find(item => String(item.id) === card.dataset.jewelId);
        if (!variant?.available || variant.price <= 0) throw new Error(copy('Ce bijou est momentanément indisponible.', 'This piece is temporarily unavailable.'));
        // A single jewel is a normal cart item, separate from a full composition.
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || copy('Ce bijou n’a pas pu être ajouté.', 'This piece could not be added.'));
        window.dispatchEvent(new CustomEvent('solstice:conversion:cart-added'));
        window.dispatchEvent(new Event('cart-updated'));
        window.dispatchEvent(new Event('cart:updated'));
      } finally {
        this.busy = false;
      }
    }
    async addToCart(title, panel) {
      if (this.busy) throw new Error(copy('Un ajout est déjà en cours.', 'An item is already being added.'));
      this.busy = true;
      const root = window.Shopify?.routes?.root || '/';
      try {
        if (panel.dataset.priced !== 'true' || panel.dataset.available !== 'true') {
          throw new Error(copy('Cette composition est momentanément indisponible à l’achat.', 'This ear stack is temporarily unavailable to buy.'));
        }
        const cards = [...panel.querySelectorAll('[data-jewel-id]')];
        const handles = [...new Set(cards.map(card => card.dataset.handle))];
        if (handles.some(handle => !handle)) throw new Error(copy('Un bijou de cette composition est indisponible.', 'A piece in this ear stack is unavailable.'));
        const products = await Promise.all(handles.map(async handle => {
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { cache: 'no-store' });
          if (!response.ok) throw new Error(copy('Impossible de vérifier les bijoux. Réessayez dans un instant.', 'The jewellery could not be checked. Please try again shortly.'));
          return response.json();
        }));
        const configId = `catalog-${crypto.randomUUID()}`;
        const items = cards.map((card, index) => {
          const product = products.find(p => p.handle === card.dataset.handle);
          const variant = product?.variants.find(v => String(v.id) === card.dataset.jewelId);
          if (!variant || !variant.available || variant.price <= 0) throw new Error(copy('Un bijou de cette composition est momentanément indisponible.', 'A piece in this ear stack is temporarily unavailable.'));
          return { id: variant.id, quantity: 1, properties: {
            Composition: title,
            Emplacement: card.dataset.placement,
            '_Configurateur': 'Oreille',
            '_ConfigID': configId,
            '_CompositionTitle': title,
            '_CompositionImage': panel.dataset.image,
            '_CompositionMaterial': panel.dataset.material || 'OR',
            '_CompositionHash': panel.dataset.hash,
            '_CompositionVariant': panel.dataset.variantId || '',
            '_Miniature': card.querySelector('img')?.src || '',
            ...(index === 0 ? { '_IsMaster': 'true' } : {})
          } };
        });
        // Add the actual variants so Shopify charges their current prices and
        // the existing inventory integration receives the real jewellery IDs.
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || copy('La composition n’a pas pu être ajoutée.', 'The ear stack could not be added.'));
        // A thumbnail failure must never turn a successful purchase into a retry.
        try {
          window.dispatchEvent(new CustomEvent('solstice:conversion:cart-added'));
          window.SolsticeCompositionImages?.schedulePhoto({ configId, imageUrl: panel.dataset.image, material: panel.dataset.material || 'OR', componentIds: items.map(item => item.id), startImmediately: true });
        } catch (_) {}
        window.dispatchEvent(new Event('cart-updated'));
        window.dispatchEvent(new Event('cart:updated'));
      } finally {
        this.busy = false;
      }
    }
  });
})();
